import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import { addNotification, setConnectionStatus, markAsRead } from '../store/notificationsSlice';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

console.log('Socket URL configured:', SOCKET_URL);

// Request notification permission
const requestNotificationPermission = async () => {
  console.log('Checking notification support...');
  console.log('Notification in window:', 'Notification' in window);
  console.log('Current permission:', Notification?.permission);
  
  if ('Notification' in window && Notification.permission === 'default') {
    try {
      console.log('Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('Notification permission result:', permission);
      
      // Show test notification if granted
      if (permission === 'granted') {
        new Notification('PulseDesk Notifications Enabled', {
          body: 'You will now receive real-time notifications',
          icon: '/logo192.png',
        });
      }
      
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }
  return Notification.permission === 'granted';
};

// Show browser notification
const showBrowserNotification = (notification, navigate, dispatch) => {
  console.log('Attempting to show browser notification...');
  console.log('Notification permission:', Notification?.permission);
  console.log('Notification data:', notification);
  
  if (!('Notification' in window)) {
    console.error('Browser does not support notifications');
    return;
  }
  
  if (Notification.permission === 'denied') {
    console.warn('Notification permission denied by user');
    return;
  }
  
  if (Notification.permission === 'default') {
    console.warn('Notification permission not granted yet');
    return;
  }
  
  if (Notification.permission === 'granted') {
    console.log('Creating browser notification...');
    try {
      const notif = new Notification(notification.title, {
        body: notification.message,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: `notification-${notification.id}`,
        requireInteraction: false,
        silent: false,
      });
      
      console.log('Browser notification created successfully');

      // Handle notification click
      notif.onclick = () => {
        console.log('Notification clicked');
        window.focus();
        
        // Mark notification as read before navigating
        if (!notification.is_read) {
          dispatch(markAsRead(notification.id));
        }
        
        // Navigate to the appropriate page
        if (notification.action_url) {
          navigate(notification.action_url);
        } else if (notification.ticket_id) {
          navigate(`/tickets/${notification.ticket_id}`);
        } else if (notification.entity_type && notification.entity_id) {
          navigate(`/${notification.entity_type}s/${notification.entity_id}`);
        }
        
        notif.close();
      };
      
      notif.onerror = (error) => {
        console.error('Notification error:', error);
      };

      // Auto close after 5 seconds
      setTimeout(() => {
        notif.close();
      }, 5000);
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  }
};

export const useNotifications = (user) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user || !user.id) {
      return;
    }

    // Request notification permission on mount
    requestNotificationPermission();

    // Initialize Socket.IO connection
    const socket = io(SOCKET_URL, {
      path: '/ws/socket.io',
      auth: {
        user_id: user.id
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    socketRef.current = socket;

    // Connection handlers
    socket.on('connect', () => {
      console.log('WebSocket connected');
      dispatch(setConnectionStatus(true));
    });

    socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      dispatch(setConnectionStatus(false));
    });

    socket.on('connected', (data) => {
      console.log('Socket authentication successful:', data);
    });

    socket.on('error', (error) => {
      console.error('Socket error:', error);
      toast.error('Notification connection error');
    });

    // Notification handler
    socket.on('notification', (notification) => {
      console.log('✅ Received notification:', notification);
      
      // Add to Redux store (this will trigger NotificationManager to show banner with sound)
      dispatch(addNotification(notification));
      
      // Dispatch custom event for ticket detail page refresh
      if (notification.ticket_id) {
        window.dispatchEvent(new CustomEvent('ticket-updated', { 
          detail: { ticketId: notification.ticket_id } 
        }));
      }
      
      // Show browser (Chrome-style) notification for desktop alerts
      // This works even when user is on another tab/window
      showBrowserNotification(notification, navigate, dispatch);
      
      // Note: We don't show toast here because NotificationBanner already handles 
      // the visual alert with sound. Toast is reserved for general app updates/changes.
    });

    socket.on('chatbot_permission_changed', (data) => {
      console.log('✅ Received chatbot permission change:', data);
      window.dispatchEvent(new CustomEvent('chatbot-permission-changed', {
        detail: { isEnabled: data.is_enabled }
      }));
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [user, dispatch, navigate]);

  return socketRef.current;
};
